import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { ApiService } from "./api/api.service";
import {
  ChatContact,
  ChatContactsParams,
  ChatMessage,
  ChatMessageCreatePayload,
  ChatMessagesParams,
  ChatMessagesReadEvent,
  ChatCreatePayload,
  ChatUpdatePayload,
  ChatItem,
  ChatParams,
} from "src/app/modules/chat/models";
import { PaginationData } from "../interfaces/orgadmin/course-organization";
import { Guid } from "../types/guid";

interface ChatUnreadSummary {
  count_unread: number;
}

@Injectable({
  providedIn: "root",
})
export class ChatService {
  private readonly incomingMessageSubject = new Subject<ChatMessage>();
  private readonly messagesReadSubject = new Subject<ChatMessagesReadEvent>();
  private readonly unreadSummarySubject = new BehaviorSubject<number>(0);
  readonly incomingMessage$ = this.incomingMessageSubject.asObservable();
  readonly messagesRead$ = this.messagesReadSubject.asObservable();
  readonly unreadCount$ = this.unreadSummarySubject.asObservable();

  constructor(private readonly apiService: ApiService) {}

  handleIncomingMessage(message: ChatMessage): void {
    this.incomingMessageSubject.next(message);

    if (!message.is_own) {
      this.unreadSummarySubject.next(this.unreadSummarySubject.value + 1);
    }
  }

  handleMessagesRead(event: ChatMessagesReadEvent): void {
    this.messagesReadSubject.next(event);
  }

  refreshUnreadSummary(): void {
    this.getUnreadSummary().subscribe({
      next: (summary) =>
        this.unreadSummarySubject.next(summary.count_unread || 0),
    });
  }

  getChats(params: ChatParams): Observable<PaginationData<ChatItem>> {
    return this.apiService.getRequest<PaginationData<ChatItem>>(
      "chats/threads/",
      {
        params: {
          ...(params.search ? { search: params.search } : {}),
          ...(params.limit ? { limit: params.limit } : {}),
          ...(params.offset ? { offset: params.offset } : {}),
          ...(params.unread ? { unread: params.unread } : {}),
        },
      },
    );
  }

  getContacts(
    params: ChatContactsParams,
  ): Observable<PaginationData<ChatContact>> {
    return this.apiService.getRequest<PaginationData<ChatContact>>(
      "chats/contacts/",
      {
        params: {
          ...(params.search ? { search: params.search } : {}),
          ...(params.limit ? { limit: params.limit } : {}),
          ...(params.offset ? { offset: params.offset } : {}),
        },
      },
    );
  }

  createChat(payload: ChatCreatePayload): Observable<ChatItem> {
    return this.apiService.postRequest<ChatItem>("chats/threads/", payload);
  }

  updateChat(chatId: Guid, payload: ChatUpdatePayload): Observable<ChatItem> {
    return this.apiService.patchRequest<ChatItem>(
      `chats/threads/${chatId}/`,
      payload,
    );
  }

  getMessages(
    chatId: Guid,
    params: ChatMessagesParams,
  ): Observable<PaginationData<ChatMessage>> {
    return this.apiService.getRequest<PaginationData<ChatMessage>>(
      `chats/threads/${chatId}/messages/`,
      {
        params: {
          ...(params.limit ? { limit: params.limit } : {}),
          ...(params.before ? { before: params.before } : {}),
        },
      },
    );
  }

  sendMessage(
    chatId: Guid,
    payload: ChatMessageCreatePayload,
  ): Observable<ChatMessage> {
    return this.apiService.postRequest<ChatMessage>(
      `chats/threads/${chatId}/messages/`,
      payload,
    );
  }

  readMessages(chatId: Guid): Observable<ChatItem> {
    return this.apiService.postRequest<ChatItem>(
      `chats/threads/${chatId}/read/`,
      {},
    );
  }

  deleteMessage(chatId: Guid, messageId: Guid): Observable<void> {
    return this.apiService.deleteRequest<void>(
      `chats/threads/${chatId}/messages/${messageId}/`,
    );
  }

  private getUnreadSummary(): Observable<ChatUnreadSummary> {
    return this.apiService.getRequest<ChatUnreadSummary>(
      "chats/unread-summary/",
    );
  }
}
